import {useCallback} from 'react';
import {useSelector, useDispatch} from 'react-redux';

import {RootState} from '../rootReducer';
import {authActions, UserDetails} from '../slices';
import {AdminUser, useApi} from '~/modules/api';

export function useAuth() {
  const dispatch = useDispatch();
  const {
    authStateKnown,
    userId,
    userEmail,
    idToken: userIdToken,
    adminUser,
    pin,
    showPinConfirm,
  } = useSelector((state: RootState) => state.auth);

  const api = useApi();

  const onSetAuth = useCallback(
    ({userId, userEmail, idToken, adminUser}: UserDetails) => {
      dispatch(
        authActions.onSetAuthState({userId, userEmail, idToken, adminUser}),
      );
    },
    [dispatch],
  );

  const onSetAdminUser = useCallback(
    ({user}: {user: AdminUser}) => {
      dispatch(authActions.onSetAdminUser({user}));
    },
    [dispatch],
  );

  const onSetAdminPin = useCallback(
    ({pin}: {pin: {lastVerified: string} | null}) => {
      dispatch(authActions.onSetAdminPin({pin}));
    },
    [dispatch],
  );

  const onSetShowPinConfirm = useCallback(
    ({showPinConfirm}: {showPinConfirm: boolean}) => {
      dispatch(authActions.onSetShowPinConfirm({showPinConfirm}));
    },
    [dispatch],
  );

  const fetchUser = useCallback(async () => {
    const response = await api.fetch({url: '/whoAmI'});
    if (response.data.status === 'success' && response.data.data.admin) {
      onSetAdminUser({
        user: response.data.data.admin,
      });
      onSetAdminPin({
        pin: response.data.data.pin ?? null,
      });
    } else {
      throw new Error(response.data.errorMessage);
    }
  }, [api, onSetAdminUser, onSetAdminPin]);

  return {
    authStateKnown,
    authenticated: !!userIdToken,
    userId,
    userIdToken,
    userEmail,
    adminUser,
    pin,
    showPinConfirm,
    onSetAuth,
    onSetAdminUser,
    onSetAdminPin,
    onSetShowPinConfirm,
    fetchUser,
  };
}
